import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, SearchX, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatVND, formatPercent } from '@/lib/format';
import { categoryDef } from '@/lib/constants';
import type { Monitor } from '@/lib/api';

export interface SkuTableProps {
  monitors: Monitor[];
  loading: boolean;
  error?: string | null;
  sort: string;
  onSortChange: (sort: string) => void;
  onSelect?: (m: Monitor) => void;
}

interface ColumnDef {
  key: string;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** API sort field name (defaults to key) */
  sortField?: string;
}

// `mobileHidden` cột sẽ ẩn ở < md (768px); `tabletHidden` ẩn ở < lg (1024px).
// Mobile chỉ giữ SKU, Sản phẩm, Hãng, Giá Web — tối thiểu để partner xem giá nhanh.
const COLUMNS: ColumnDef[] = [
  { key: 'sku', label: 'SKU', sortable: false, className: 'sticky left-0 bg-card z-10 min-w-[160px]' },
  { key: 'name', label: 'Sản phẩm', sortable: true, className: 'min-w-[240px]' },
  { key: 'brand', label: 'Hãng', sortable: true, className: 'min-w-[90px]' },
  { key: 'size', label: 'Size', sortable: true, align: 'right', className: 'hidden min-w-[70px] md:table-cell' },
  { key: 'resolution', label: 'Độ PG', sortable: false, align: 'center', className: 'hidden min-w-[70px] md:table-cell' },
  { key: 'refresh_rate', label: 'Hz', sortable: true, align: 'right', className: 'hidden min-w-[70px] md:table-cell' },
  { key: 'panel_type', label: 'Panel', sortable: false, className: 'hidden min-w-[100px] lg:table-cell' },
  { key: 'tags', label: 'Tags', sortable: false, className: 'hidden min-w-[140px] lg:table-cell' },
  { key: 'price_ct', label: 'Giá CT', sortable: true, align: 'right', className: 'hidden min-w-[110px] md:table-cell' },
  { key: 'price_web', label: 'Giá Web', sortable: true, align: 'right', className: 'min-w-[110px]' },
  { key: 'margin_pct', label: '% LN', sortable: true, align: 'right', className: 'hidden min-w-[80px] md:table-cell' },
  { key: 'npp', label: 'NPP', sortable: false, className: 'hidden min-w-[80px] lg:table-cell' },
  { key: 'action', label: '', sortable: false, align: 'right', className: 'min-w-[50px]' },
];

const TAG_LABEL: Record<string, string> = {
  'usb-c': 'USB-C',
  gsync: 'G-Sync',
  hdr: 'HDR',
  '16:10': '16:10',
};

function parseSort(sort: string): { field: string; dir: 'asc' | 'desc' } {
  const [field, dir] = sort.split(':');
  return { field: field ?? 'name', dir: (dir as 'asc' | 'desc') ?? 'asc' };
}

interface SortHeaderProps {
  col: ColumnDef;
  current: { field: string; dir: 'asc' | 'desc' };
  onSortChange: (sort: string) => void;
}

function SortHeader({ col, current, onSortChange }: SortHeaderProps) {
  const sortField = col.sortField ?? col.key;
  const isActive = current.field === sortField;

  if (!col.sortable) {
    return <span className={cn(col.align === 'right' && 'block text-right', col.align === 'center' && 'block text-center')}>{col.label}</span>;
  }

  const handleClick = () => {
    if (!isActive) {
      onSortChange(`${sortField}:asc`);
      return;
    }
    onSortChange(`${sortField}:${current.dir === 'asc' ? 'desc' : 'asc'}`);
  };

  const Icon = !isActive ? ArrowUpDown : current.dir === 'asc' ? ArrowUp : ArrowDown;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded px-1 -mx-1 transition-colors hover:bg-accent hover:text-accent-foreground',
        isActive && 'text-foreground',
        col.align === 'right' && 'flex-row-reverse'
      )}
    >
      <span>{col.label}</span>
      <Icon className={cn('h-3 w-3', !isActive && 'opacity-40')} />
    </button>
  );
}

function alignClass(align?: 'left' | 'right' | 'center'): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return '';
}

export function SkuTable({ monitors, loading, error, sort, onSortChange, onSelect }: SkuTableProps) {
  const current = parseSort(sort);

  const tableHeader = (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        {COLUMNS.map((col) => (
          <TableHead key={col.key} className={cn(col.className, alignClass(col.align))}>
            <SortHeader col={col} current={current} onSortChange={onSortChange} />
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  );

  if (loading) {
    return (
      <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
        <Table>
          {tableHeader}
          <TableBody>
            {Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                {COLUMNS.map((col) => (
                  <TableCell key={col.key} className={cn(col.className, alignClass(col.align))}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card py-16 text-center">
        <div className="rounded-full bg-destructive/10 p-3 text-destructive">
          <SearchX className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Lỗi tải dữ liệu</h3>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!monitors.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card py-16 text-center">
        <div className="rounded-full bg-muted p-3 text-muted-foreground">
          <SearchX className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Không có SKU nào</h3>
          <p className="mt-1 text-sm text-muted-foreground">Thử bỏ bớt filter hoặc đổi từ khóa tìm kiếm.</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={250}>
      <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
        <Table>
          {tableHeader}
          <TableBody>
            {monitors.map((m) => {
              const cat = categoryDef(m.category);
              const tags = (m.tags ?? '').split(',').filter(Boolean);
              const handleRowClick = () => onSelect?.(m);
              return (
                <TableRow
                  key={m.id}
                  className="cursor-pointer"
                  onClick={handleRowClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowClick();
                    }
                  }}
                  tabIndex={0}
                >
                  <TableCell className="sticky left-0 z-10 bg-card text-xs group-hover:bg-muted/50">
                    <div className="flex items-center gap-1.5">
                      {Boolean(m.is_focal) && <Star className="h-3 w-3 shrink-0 fill-cat-focal text-cat-focal" />}
                      <span className="truncate">{m.id}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-start gap-2">
                      {cat && <cat.icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', cat.colorClass)} />}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="line-clamp-2 text-sm leading-tight">{m.name}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-md">
                          {m.name}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{m.brand}</TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">
                    {m.size ? `${m.size}"` : '—'}
                  </TableCell>
                  <TableCell className="hidden text-center text-xs font-medium md:table-cell">{m.resolution ?? '—'}</TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">
                    {m.refresh_rate ? `${m.refresh_rate}` : '—'}
                  </TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">{m.panel_type ?? '—'}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {tags.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-0.5">
                        {tags.map((t) => (
                          <Badge key={t} variant="outline" className="h-5 border-border bg-background px-1.5 py-0 text-[10px]">
                            {TAG_LABEL[t] ?? t}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">{formatVND(m.price_ct)}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">
                    {formatVND(m.price_web)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'hidden text-right tabular-nums md:table-cell',
                      m.margin_pct != null && m.margin_pct < 0 && 'text-destructive',
                      m.margin_pct != null && m.margin_pct >= 10 && 'text-cat-graphic'
                    )}
                  >
                    {formatPercent(m.margin_pct)}
                  </TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">{m.npp ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick();
                          }}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Xem chi tiết</TooltipContent>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
